import { useState, useRef, useEffect } from 'react';

interface UseCompanyCardAnimationProps {
  companyId: string;
  isPreRender: boolean;
}

export const useCompanyCardAnimation = ({ companyId, isPreRender }: UseCompanyCardAnimationProps) => {
  const [isFlying, setIsFlying] = useState(false);
  const [flyDirection, setFlyDirection] = useState<'good' | 'bad' | null>(null);
  const [flyTarget, setFlyTarget] = useState<{ x: number, y: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Expose position for pre-rendered items and update coordinates continuously
  useEffect(() => {
    if (isPreRender && cardRef.current) {
      const updateCoordinates = () => {
        const rect = cardRef.current!.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        
        // Store coordinates on the element for external access
        (cardRef.current as any)._coordinates = { x: centerX, y: centerY };
      };
      
      updateCoordinates();
      
      // Update coordinates when layout changes
      const observer = new ResizeObserver(updateCoordinates);
      observer.observe(cardRef.current);
      
      return () => observer.disconnect();
    }
  }, [isPreRender]);

  const startFlyAnimation = (vote: 'good' | 'bad') => {
    // Find the target position by looking for pre-rendered items
    const votedSection = document.querySelector('[data-voted-section="true"]');
    let targetCoords = null;
    
    if (votedSection) {
      const preRenderedItems = votedSection.querySelectorAll('[data-company-id]');
      const targetItem = Array.from(preRenderedItems).find(item => 
        item.getAttribute('data-company-id') === companyId
      );
      
      if (targetItem && (targetItem as any)._coordinates) {
        targetCoords = (targetItem as any)._coordinates;
        
        // Get current position for relative calculation
        const currentRect = cardRef.current!.getBoundingClientRect();
        const currentCenterX = currentRect.left + currentRect.width / 2;
        const currentCenterY = currentRect.top + currentRect.height / 2;
        
        targetCoords = {
          x: targetCoords.x - currentCenterX,
          y: targetCoords.y - currentCenterY
        };
      }
    }

    setIsFlying(true);
    setFlyDirection(vote);
    setFlyTarget(targetCoords);
    
    return targetCoords;
  };

  const flyStyle = flyTarget ? {
    '--fly-target-x': `${flyTarget.x}px`,
    '--fly-target-y': `${flyTarget.y}px`
  } as React.CSSProperties : {};

  return {
    cardRef,
    isFlying,
    flyDirection,
    flyStyle,
    startFlyAnimation
  };
}; 